/**
 * 학생별 순찰 기록 (2026-10-02 대표 지시) — 출석부 좌석 상세에 붙는다.
 * 순찰 QR 점검을 마칠 때마다 그 시각 상태(공부중·자리비움·외출·사유서)가 한 칸씩 쌓인다.
 * 학부모·학생 앱, 순찰 태블릿과 같은 서버 기록(patrol_student_checks)을 본다.
 */
import React, { useEffect, useState } from 'react';
import { apiClient } from '../../../../services/authApi';

export interface PatrolCheck {
  time: string;
  status: 'studying' | 'outing' | 'excused' | 'away' | string;
  label: string;
  items?: string | null;
  zone?: string | null;
  staff_name?: string | null;
}

export const PATROL_ICON: Record<string, string> = {
  studying: '😊', outing: '🚶', excused: '📄', away: '❌',
};
const COLOR: Record<string, string> = {
  studying: 'text-blue-600', outing: 'text-amber-600', excused: 'text-violet-600', away: 'text-red-600',
};

const kstToday = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
const shift = (d: string, n: number) => {
  const t = new Date(`${d}T12:00:00+09:00`);
  t.setDate(t.getDate() + n);
  return t.toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' });
};

export const PatrolChecksStrip: React.FC<{ studentId: string }> = ({ studentId }) => {
  const [day, setDay] = useState(kstToday());
  const [checks, setChecks] = useState<PatrolCheck[] | null>(null);
  const [end, setEnd] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setChecks(null);
    apiClient
      .get<{ checks: PatrolCheck[]; schedule_end: string | null }>(
        `/v1/owner/patrol-qr/students/${studentId}/checks?date=${day}`,
      )
      .then((r) => { if (alive) { setChecks(r.data.checks || []); setEnd(r.data.schedule_end); } })
      .catch(() => { if (alive) setChecks([]); });
    return () => { alive = false; };
  }, [studentId, day]);

  const isToday = day === kstToday();
  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold text-gray-700">🚶 순찰 기록</span>
        <button className="text-xs px-1.5 border rounded" onClick={() => setDay(shift(day, -1))}>◀</button>
        <span className="text-xs text-gray-600">{isToday ? '오늘' : day}</span>
        <button className="text-xs px-1.5 border rounded disabled:opacity-30" disabled={isToday}
          onClick={() => setDay(shift(day, 1))}>▶</button>
        {checks && checks.length > 0 && (
          <span className="ml-auto text-xs text-gray-500">
            {checks.length}회 · 자리비움 {checks.filter((c) => c.status === 'away').length}회
          </span>
        )}
      </div>
      {checks === null ? (
        <div className="text-xs text-gray-400">불러오는 중…</div>
      ) : checks.length === 0 ? (
        <div className="text-xs text-gray-400">이 날 순찰 기록이 없습니다. 순찰 QR 점검을 마치면 여기에 쌓입니다.</div>
      ) : (
        <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
          {checks.map((c, i) => (
            <div key={i} className="flex flex-col items-center text-center"
              title={`${c.time} ${c.label}${c.items ? ` · ${c.items}` : ''}${c.zone ? ` · ${c.zone}` : ''}${c.staff_name ? ` · 순찰 ${c.staff_name}` : ''}`}>
              <span className="text-xl leading-none">{PATROL_ICON[c.status] || '•'}</span>
              <span className={`text-[10px] leading-tight mt-0.5 ${COLOR[c.status] || 'text-gray-600'}`}>{c.label}</span>
              {c.items && <span className="text-[10px] leading-tight text-orange-600">{c.items}</span>}
              <span className="text-[10px] text-gray-500">{c.time}</span>
            </div>
          ))}
          {end && (
            <div className="flex flex-col items-center text-center">
              <span className="text-xl leading-none">🕙</span>
              <span className="text-[10px] leading-tight mt-0.5 text-gray-600">스케줄 종료</span>
              <span className="text-[10px] text-gray-500">{end}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PatrolChecksStrip;
